import { useQuery, useQueryClient } from '@tanstack/react-query';
import { uniqBy } from 'lodash';
import { useContext, useEffect, useMemo, useRef } from 'react';

import { SocketContext } from '../providers/SocketProvider/SocketProvider';
import { ById, PartialParticipant } from '../services/backendService/types';
import { newRelicNoticeError } from './useNewRelic';

const STALE_TIME = 10 * 60 * 1000; // 10 minutes

export const useGameParticipants = (gameId: string, tenantId: string) => {
  // This hook is used to get the participants of a game and subscribe to changes
  // It is a singleton, meaning that it will only subscribe once and the subscription will be kept
  // so dont use this hook more than one time on the same page
  const { gameAPI } = useContext(SocketContext);
  const queryClient = useQueryClient();

  const participantsQueryKey = useMemo(() => ['game-participants', tenantId, gameId], [
    tenantId,
    gameId,
  ]);
  const countQueryKey = useMemo(() => ['game-participants-count', tenantId, gameId], [
    tenantId,
    gameId,
  ]);

  const participantsRef = useRef<PartialParticipant[]>([]);
  const intervalRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!gameId || !gameAPI) return;

    const fetchParticipants = async ({ volatile }: { volatile: boolean }) => {
      const allParticipants = await gameAPI.game.listAllParticipants({
        gameId,
        tenantId,
        volatile,
      });
      if (!allParticipants) {
        queryClient.setQueryData<PartialParticipant[]>(participantsQueryKey, []);
        participantsRef.current = [];
        queryClient.setQueryData<number>(countQueryKey, 0);
        return;
      }
      queryClient.setQueryData<PartialParticipant[]>(
        participantsQueryKey,
        allParticipants,
      );
      queryClient.setQueryData<number>(countQueryKey, allParticipants.length);
      participantsRef.current = allParticipants;
    };

    fetchParticipants({ volatile: false });

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(
      () => fetchParticipants({ volatile: true }),
      1000 * 60,
    );

    const subChange = gameAPI.game
      .listChangedParticipants({ gameId, tenantId })
      .subscribe({
        next: rawParticipants => {
          // updates the participants list with the new participants and the existing one
          if (!rawParticipants || rawParticipants.length === 0) {
            return;
          }

          // merges the rawParticipants into the existing participants list and avoid duplicates
          const newParticipants = uniqBy(
            [...rawParticipants, ...participantsRef.current],
            'id',
          );
          queryClient.setQueryData<PartialParticipant[]>(
            participantsQueryKey,
            newParticipants,
          );
          participantsRef.current = newParticipants;
        },
        error: error => {
          newRelicNoticeError(
            error,
            'Error while listening to game participants changes',
          );
        },
      });

    const subCount = gameAPI.game.countParticipants({ gameId, tenantId }).subscribe({
      next: nParticipants => {
        // updates the participants list with the new participants and the existing one
        if (nParticipants !== undefined) {
          queryClient.setQueryData<number>(countQueryKey, nParticipants);
        }
      },
      error: error => {
        newRelicNoticeError(error, 'Error while listening to game participants count');
      },
    });

    return () => {
      subChange.unsubscribe();
      subCount.unsubscribe();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [gameAPI, gameId, tenantId, queryClient, participantsQueryKey, countQueryKey]);

  const { data: participants } = useQuery<PartialParticipant[] | undefined>({
    queryKey: participantsQueryKey,
    queryFn: () => Promise.resolve(undefined),
    staleTime: STALE_TIME,
    enabled: Boolean(gameId && gameAPI),
  });

  const { data: numberOfParticipants } = useQuery<number>({
    queryKey: countQueryKey,
    queryFn: () => Promise.resolve(0),
    staleTime: STALE_TIME,
    enabled: Boolean(gameId && gameAPI),
  });

  const byId = useMemo(
    () =>
      (participants || []).reduce<ById<PartialParticipant>>(
        (acc, cur) => ({ ...acc, [cur.id]: cur }),
        {},
      ),
    [participants],
  );
  const participantsMemo = useMemo(() => (participants || []).filter(p => !p.isKicked), [
    participants,
  ]);

  return {
    allParticipants: participants || [],
    participants: participantsMemo,
    count: numberOfParticipants || 0,
    byId,
  };
};
